// Works out whether a winery is open right now, in Gatlinburg (Eastern) time,
// from the free-text hours stored on each location.
//
// Understood formats:
//   "Open 10AM-11PM daily"
//   "Open 10AM-9PM daily, 10AM-7PM Sundays"
//   "10AM - 11PM"

const TRAIL_TIME_ZONE = "America/New_York";

export type OpenState = {
  isOpen: boolean;
  /** Short label, e.g. "Open now" or "Closed - opens 10AM". */
  label: string;
};

type Range = { openMinutes: number; closeMinutes: number };

function toMinutes(hour: number, minute: number, meridiem: string): number {
  let h = hour % 12;
  if (meridiem.toLowerCase() === "pm") h += 12;
  return h * 60 + minute;
}

function formatTime(minutes: number): string {
  const h24 = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const meridiem = h24 >= 12 ? "PM" : "AM";
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return m === 0 ? `${h}${meridiem}` : `${h}:${String(m).padStart(2, "0")}${meridiem}`;
}

const RANGE_RE =
  /(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\s*(?:-|–|to)\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/gi;

/** Pull every time range out of an hours string, in order. */
function parseRanges(hours: string): Range[] {
  const ranges: Range[] = [];
  for (const match of hours.matchAll(RANGE_RE)) {
    const [, oh, om, ome, ch, cm, cme] = match;
    if (!oh || !ome || !ch || !cme) continue;
    ranges.push({
      openMinutes: toMinutes(Number(oh), Number(om ?? 0), ome),
      closeMinutes: toMinutes(Number(ch), Number(cm ?? 0), cme),
    });
  }
  return ranges;
}

/** Current day (0 = Sunday) and minutes past midnight, in Gatlinburg time. */
function nowInTrailTime(now: Date): { day: number; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TRAIL_TIME_ZONE,
    weekday: "short",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).formatToParts(now);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const day = Math.max(0, days.indexOf(get("weekday")));
  const hour = Number(get("hour")) % 24;
  const minute = Number(get("minute"));
  return { day, minutes: hour * 60 + minute };
}

/**
 * Returns the open/closed state for an hours string, or null when the string
 * can't be understood (in which case no badge should be shown).
 */
export function getOpenState(hours: string | null | undefined, now = new Date()): OpenState | null {
  if (!hours) return null;

  const ranges = parseRanges(hours);
  if (ranges.length === 0) return null;

  const { day, minutes } = nowInTrailTime(now);

  // A second range that mentions Sunday applies on Sundays only.
  const sundayMentioned = /sun/i.test(hours);
  const range =
    day === 0 && sundayMentioned && ranges[1] ? ranges[1] : ranges[0];
  if (!range) return null;

  const { openMinutes, closeMinutes } = range;
  // Handle closing after midnight (e.g. 10AM-1AM).
  const closesNextDay = closeMinutes <= openMinutes;
  const isOpen = closesNextDay
    ? minutes >= openMinutes || minutes < closeMinutes
    : minutes >= openMinutes && minutes < closeMinutes;

  const opensLater = minutes < openMinutes;

  return {
    isOpen,
    label: isOpen
      ? `Open now until ${formatTime(closeMinutes)}`
      : opensLater
        ? `Opens ${formatTime(openMinutes)} today`
        : `Opens ${formatTime(openMinutes)} tomorrow`,
  };
}
